import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import Link from 'next/link'
import { Quote } from 'lucide-react'
import { LogoMark } from '@/components/generals/logo'
import { SectionHeading } from '@/components/generals/section-heading'

interface TestimonialsSectionProps {
    testimonials: Testimonial[]
}

export const TestimonialsSection: FC<TestimonialsSectionProps> = ({ testimonials }): ReactNode => {
    if (testimonials.length === 0) return null

    return (
        <section id='testimonials' className='py-20 lg:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    eyebrow="Owner stories"
                    title="Life in a Roamwood home"
                    description="The best part of our job is hearing how people live in the homes we build. Here is what a few of them told us."
                />

                <div className='mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3'>
                    {testimonials.map(testimonial => (
                        <figure
                            key={testimonial.id}
                            className='flex flex-col rounded-2xl border border-border bg-card p-7 shadow-[0_1px_2px_rgb(0_0_0/0.04)] sm:p-8'
                        >
                            <Quote className='size-8 text-timber' aria-hidden='true' />

                            <blockquote className='mt-6 flex-1'>
                                <p className='font-heading text-xl leading-snug font-normal text-pretty' data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>{testimonial.quote}</p>
                            </blockquote>

                            <figcaption className='mt-8 flex items-center gap-3.5 border-t border-border pt-6'>
                                <span aria-hidden='true' className='flex size-11 shrink-0 items-center justify-center rounded-full bg-secondary text-primary'>
                                    <LogoMark className='size-6' />
                                </span>

                                <span className='min-w-0'>
                                    <span className='block font-semibold' data-mdlfy-cms={`testimonials:${testimonial.id}:author`}>{testimonial.author}</span>

                                    {(testimonial.context || testimonial.model_name) && (
                                        <span className='mt-0.5 block text-sm text-muted-foreground'>
                                            {testimonial.context && (
                                                <span data-mdlfy-cms={`testimonials:${testimonial.id}:context`}>{testimonial.context}</span>
                                            )}

                                            {testimonial.context && testimonial.model_name && (
                                                <span aria-hidden='true'> &middot; </span>
                                            )}

                                            {testimonial.model_name && testimonial.model_slug && testimonial.model_id && (
                                                <Link
                                                    href={`/models/${testimonial.model_slug}`}
                                                    className='font-medium text-primary underline-offset-4 hover:underline'
                                                >
                                                    <span data-mdlfy-cms={`models:${testimonial.model_id}:name`}>{testimonial.model_name}</span>
                                                </Link>
                                            )}
                                        </span>
                                    )}
                                </span>
                            </figcaption>
                        </figure>
                    ))}
                </div>
            </div>
        </section>
    )
}
